import React, { useState, useEffect, useRef } from 'react';
import { Edit2, Trash2, Search, Plus, SortAsc, SortDesc, AlertTriangle } from 'lucide-react';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptyApprovers = message.startsWith('No approvers found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        borderRadius: isEmptyApprovers ? '16px' : '8px',
        border: isEmptyApprovers ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: isEmptyApprovers ? '16.8px' : '18px',
          lineHeight: isEmptyApprovers ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...DialogStyles.button,
              backgroundColor: isEmptyApprovers ? '#FFFDD0' : '#FFFDD0',
              width: isEmptyApprovers ? '120px' : '100%',
              margin: isEmptyApprovers ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const ManageApproversDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  selectedLevelId,
  corporateAccount,
  currentProject,
  directLinkPath,
  login_token,
  requirementIdWithPrefix,
    req_type,
  functionalLevelDescription
}) => {
  const [approvers, setApprovers] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [approverLevel, setApproverLevel] = useState('ProjectLevel');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [searchTerm, setSearchTerm] = useState('')
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [sortCriteria, setSortCriteria] = useState('created_date desc');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);

  // Delete confirmation dialog state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);



  // New permissions state
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canDelete: false
  });

  const userSearchRef = useRef(null);


const generateRequirementDirectLink = (reqId, levelId, projectId) => {
  const baseUrl = window.location.origin;



  if (reqId)
  {
    return `${baseUrl}${directLinkPath}?project=${encodeURIComponent(projectId)}&reqId=${encodeURIComponent(reqId)}&action=edit`;
    }
  else if (levelId)
  {
    return `${baseUrl}${directLinkPath}?project=${encodeURIComponent(projectId)}&levelId=${encodeURIComponent(levelId)}`;
    }
  else
  {
    return `${baseUrl}${directLinkPath}?project=${encodeURIComponent(projectId)}`;
    }

};


  // Permissions check function (similar to ManageLinksDialog)
  const checkPermission = async (categoryHeader) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: localStorage.getItem('user_id'),
          project_id: currentProject,
          category_header: categoryHeader,
          category_sub_header: []
        }),
      });
      const data = await response.json();
      console.log('API Response:', data);

      // Create new permissions object
      const newPermissions = {
        canCreate: false,
        canDelete: false
      };

      // Map API response to permission fields
      data.user_actions_list.forEach(item => {
        switch(item.user_action) {
          case 'Create Approvers':
            newPermissions.canCreate = item.has_access;
            break;
          case 'Delete Approvers':
            newPermissions.canDelete = item.has_access;
            break;
        }
      });

      console.log('New Permissions:', newPermissions);
      console.log('selectedRequirements:', selectedRequirements);
        console.log('selectedLevelId:', selectedLevelId);

      // Update the state with the new permissions
      setPermissions(newPermissions);

      return data.access_status && data.status === 'Success';
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return false;
    }
  };

  // Helper function to ensure data is fully reset
  const resetAllData = () => {
    setApprovers([]);
    setSelectedApprovers(new Set());
    setError('');
    setIsLoading(false);
    setSearchTerm('');
  };

  const showErrorDialog = (message) => {
    setDialogMessage(message);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    if (pendingDeleteAction) {
      pendingDeleteAction();
      setPendingDeleteAction(null);
    }
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
    setPendingDeleteAction(null);
  };



  // Initialize component when dialog opens
  useEffect(() => {
    const initializeDialog = async () => {
      if (isOpen && !isInitialized) {
        // Reset all data
        resetAllData();
        setIsLoading(true);

        // Check permissions first
        await checkPermission('Manage Approvers');




    // Map req_type to approval level
let initialLevel = 'ProjectLevel';
switch (req_type.toUpperCase()) {
  case 'PROJECT LEVEL':
    initialLevel = 'ProjectLevel';  // Fixed: was 'Project'
    break;
  case 'FUNCTIONAL LEVEL':
    initialLevel = 'FunctionalLevel';
    break;
  case 'REQUIREMENT':
  case 'INTEGRATION REQUIREMENT':
    initialLevel = 'RequirementLevel';  // Fixed: was 'Requirement'
    break;
  default:
    initialLevel = 'ProjectLevel';  // Fixed: was 'Project'
}

console.log('Initial Approver Level:', initialLevel);



        // Set the approver level
        setApproverLevel(initialLevel);

        await fetchApprovers(initialLevel);
        await fetchAvailableUsers();

        setIsInitialized(true);
      } else if (!isOpen) {
        // Reset initialization when dialog closes
        setIsInitialized(false);
      }
    };

    initializeDialog();
  }, [isOpen]);

  // This effect handles changes in approver level
  useEffect(() => {
    const handleLevelChange = async () => {
      if (isOpen && isInitialized) {
        setApprovers([]);
        setSelectedApprovers(new Set());
        setError('');
        setIsLoading(true);

        await fetchApprovers(approverLevel);
      }
    };

    handleLevelChange();
  }, [approverLevel]);

  const fetchAvailableUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_user_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount
        })
      });

      const data = await response.json();

        console.log('Available Users Response:', data);

      if (data.status === 'Success') {
        setAvailableUsers(data.user_list || []);
      } else {
        throw new Error(data.status_description || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
//      setError('Failed to load available users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchApprovers = async (level) => {
    // Set loading state if not already set
    setApprovers([]);

    if (!isLoading) {
      setIsLoading(true);
    }

    // Determine parameters based on level
    let levelId = null;
    let reqId = null;

    if (level === 'FunctionalLevel') {
      if (!selectedLevelId) {
        setError('Please select a functional level for Functional Level Approvers');
        setIsLoading(false);
        return;
      }
      levelId = selectedLevelId;
    } else if (level === 'RequirementLevel') {
      if (selectedRequirements.size !== 1) {
        setError('Please select exactly one requirement for Requirement Level Approvers');
        setIsLoading(false);
        return;
      }
      reqId = Array.from(selectedRequirements)[0];
    }

    try {
      const response = await fetch(appConfig.getUrl('/get_requirements_approver_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: levelId || '',
          req_id: reqId || '',
          search_query: searchTerm,
          sort_criteria: sortCriteria
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setApprovers(data.approver_list || []);
        if (data.approver_list?.length === 0) {
          showErrorDialog('No approvers found.\nCreate new approvers to get started.');
        }
      } else {
        setApprovers([]);
        // Silently handle error - don't throw
      }
    } catch (err) {
      setApprovers([]);
      setError(err.message);
      console.error('Error fetching approvers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproverLevelChange = (newLevel) => {
    if (newLevel !== approverLevel) {
      setApproverLevel(newLevel);
    }
  };

  const handleOpenCreateDialog = () => {
    if (!permissions.canCreate) {
      return; // Don't open dialog if no permission
    }
    setSelectedUser('');
    setUserSearchTerm('');
    setError('');
    setIsCreateDialogOpen(true);
  };

  // Handle search input with Enter key support
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    // Trigger refresh of approvers list
    fetchApprovers(approverLevel);
  };

  // Effect to handle search term changes - debounced search
  useEffect(() => {
    if (isOpen && isInitialized) {
      const debounceTimer = setTimeout(() => {
        fetchApprovers(approverLevel);
      }, 500); // 500ms debounce

      return () => clearTimeout(debounceTimer);
    }
  }, [searchTerm, sortCriteria, isOpen, isInitialized, approverLevel]);








const handleAddApprover = async () => {
  setIsLoading(true);
  setError('');

  // Validate input
  if (!selectedUser) {
    setError('Please select a user');
    setIsLoading(false);
    return;
  }

  // Determine parameters based on level
  let levelId = null;
  let reqId = null;

  if (approverLevel === 'FunctionalLevel') {
    if (!selectedLevelId) {
      setError('Please select a functional level');
      setIsLoading(false);
      return;
    }
    levelId = selectedLevelId;
  } else if (approverLevel === 'RequirementLevel') {
    if (selectedRequirements.size !== 1) {
      setError('Please select exactly one requirement');
      setIsLoading(false);
      return;
    }
    reqId = Array.from(selectedRequirements)[0];
  }

  try {
    // Generate direct link for the requirement (if applicable)
    let itemDirectLink = '';
    itemDirectLink = generateRequirementDirectLink(reqId, levelId, currentProject);

    const response = await fetch(appConfig.getUrl('/add_requirement_approver'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        level_id: levelId || '',
        req_id: reqId || '',
        approval_user_id: selectedUser,
        item_direct_link: itemDirectLink // Add the direct link
      })
    });

    const data = await response.json();
    if (data.status === 'Success') {
      setError(data.status_description || 'Approver added successfully');

      // Delay the refresh to allow user to see the success message
      setTimeout(() => {
        setIsCreateDialogOpen(false);
        setSelectedUser('');
        setUserSearchTerm('');
        setError('');

        // Refresh the approvers list
        fetchApprovers(approverLevel);
      }, 2000);
    } else {
      throw new Error(data.status_description || 'Failed to add approver');
    }
  } catch (err) {
    setError(err.message);
    console.error('Error adding approver:', err);
  } finally {
    setIsLoading(false);
  }
};










  // Delete confirmation dialog handler
  const handleDeleteSelectedConfirm = () => {
    if (selectedApprovers.size === 0) {
      showErrorDialog('Please select at least one approver to delete');
      return;
    }

    if (!permissions.canDelete) {
      return; // Don't proceed if no permission
    }

    setIsDeleteConfirmOpen(true);
  };






 const handleDeleteSelected = async () => {
  setIsDeleteConfirmOpen(false);
  setIsLoading(true);
  setError('');

  // Determine parameters based on level
  let levelId = null;
  let reqId = null;

  if (approverLevel === 'FunctionalLevel') {
    levelId = selectedLevelId;
  } else if (approverLevel === 'RequirementLevel') {
    reqId = Array.from(selectedRequirements)[0];
  }

  const promises = [];

  // Generate direct link for the requirement (if applicable)
  let itemDirectLink = '';
  itemDirectLink = generateRequirementDirectLink(reqId, levelId, currentProject);

  // Create deletion promises for each selected approver
  for (const approverUserId of selectedApprovers) {
    promises.push(
      fetch(appConfig.getUrl('/delete_requirement_approver'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: levelId || '',
          req_id: reqId || '',
          approval_user_id: approverUserId,
          item_direct_link: itemDirectLink // Add the direct link
        })
      })
    );
  }

  try {
    // Wait for all delete operations to complete
    const responses = await Promise.all(promises);
    const results = await Promise.all(responses.map(r => r.json()));

    // Check if any operation failed
    const failures = results.filter(r => r.status !== 'Success');

    if (failures.length > 0) {
      throw new Error(`Failed to delete ${failures.length} approvers`);
    }

    setError('Approvers deleted successfully');
    setSelectedApprovers(new Set());

    // Delay the refresh to allow user to see the success message
    setTimeout(() => {
      setError('');
      // Refresh the approvers list
      fetchApprovers(approverLevel);
    }, 2000);
  } catch (err) {
    setError(err.message);
    console.error('Error deleting approvers:', err);
  } finally {
    setIsLoading(false);
  }
};








  // Focus input when dialog opens
  useEffect(() => {
    if (isCreateDialogOpen && userSearchRef.current) {
      userSearchRef.current.focus();
    }
  }, [isCreateDialogOpen]);

  // Delete confirmation dialog component
  const renderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmOpen) return null;

    return (
      <div style={{...DialogStyles.overlay, zIndex: 1100}}>
        <div style={{...DialogStyles.dialog, width: '500px'}}>
          <div style={{...DialogStyles.title, backgroundColor: '#FFE6E6', color: '#CC0000', padding: '15px 0'}}>
            <div className="flex items-center justify-center gap-2">
              <AlertTriangle size={20} />
              <span>Confirm Deletion</span>
            </div>
          </div>

          <div className="px-6 py-4">
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete {selectedApprovers.size} selected approver{selectedApprovers.size !== 1 ? 's' : ''}?
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>

            {/* Show some details about selected approvers */}
            <div className="bg-gray-50 rounded p-3 mb-4 max-h-32 overflow-y-auto">
              <p className="text-xs text-gray-600 mb-2">Selected approvers:</p>
              {Array.from(selectedApprovers).slice(0, 5).map(userId => {
                const approver = approvers.find(a => a.approval_user_id === userId);
                return approver ? (
                  <div key={userId} className="text-xs text-gray-700 mb-1">
                    {approver.user_name} ({approver.approval_user_id})
                  </div>
                ) : null;
              })}
              {selectedApprovers.size > 5 && (
                <div className="text-xs text-gray-500">
                  ... and {selectedApprovers.size - 5} more
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-center gap-4 p-6 pt-0">
            <button
              onClick={() => setIsDeleteConfirmOpen(false)}
              style={{
                backgroundColor: '#f8f9fa',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSelected}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Delete {selectedApprovers.size} Approver{selectedApprovers.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Create Approver Dialog Content
  const renderCreateApproverDialog = () => {
    if (!isCreateDialogOpen) return null;

    return (
<div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
  <div className="bg-white rounded-lg max-w-screen-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
    <h1 className="text-l font-bold text-black bg-[#CCCCFF] text-center flex items-center justify-center rounded-t-lg" style={{ height: '3em' }}>
      Add Approver
    </h1>

          {/* Level Information */}
          <div className="mb-6  ml-8 mr-8 p-4 border border-gray-300 rounded-md">
            <h3 className="text-base font-semibold mb-3">Adding approver to:</h3>
            {approverLevel === 'ProjectLevel' ? (
              <p>Project Level (Overall project level approvers)</p>
            ) : approverLevel === 'FunctionalLevel' ? (
              <p>Functional Level: <strong>{functionalLevelDescription || 'Select a functional level'}</strong></p>
            ) : (
              <p>Requirement: <strong>{requirementIdWithPrefix || 'Select a requirement'}</strong></p>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleAddApprover(); }}>
            {/* User Selection with Type-ahead Search */}
            <div className="mb-4 ml-8 mr-8">
              <label className="block text-sm font-medium mb-2">
                Select User: <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Start typing to search users..."
                  value={userSearchTerm}
                  ref={userSearchRef}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full p-2 border rounded mb-1"
                  disabled={isLoadingUsers || isLoading}
                />

                {isLoadingUsers ? (
                  <p className="text-sm text-gray-500 mt-1">Loading users...</p>
                ) : (
                  <div className="max-h-60 overflow-y-auto border rounded">
                    {availableUsers
                      .filter(user =>
                        userSearchTerm === '' ||
                        user.user_name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                        user.user_id.toLowerCase().includes(userSearchTerm.toLowerCase())
                      )
                      .map(user => (
                        <div
                          key={user.user_id}
                          className={`p-2 cursor-pointer hover:bg-blue-50 ${selectedUser === user.user_id ? 'bg-blue-100' : ''}`}
                          onClick={() => {
                            setSelectedUser(user.user_id);
                            setUserSearchTerm(`${user.user_name} (${user.user_id})`);
                          }}
                        >
                          {user.user_name} ({user.user_id})
                        </div>
                      ))}

                    {userSearchTerm !== '' &&
                     availableUsers.filter(user =>
                       user.user_name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                       user.user_id.toLowerCase().includes(userSearchTerm.toLowerCase())
                     ).length === 0  }

                    {userSearchTerm === '' && (
                      <div className="p-2 text-gray-500">Type to search for users</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className={'mb-4 p-2 rounded ' + (
                error.includes('successfully')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-500'
              )}>
                {error}
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setSelectedUser('');
                  setUserSearchTerm('');
                  setError('');
                }}
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading}
              >
                Close
              </button>

              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: permissions.canCreate ? '#C8F4DE' : '#CCCCCC',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor:   'pointer'
                }}
                disabled={isLoading || !permissions.canCreate}
              >
                {isLoading ? 'Processing...' : 'Add Approver'}
              </button>
            </div>

            {!isLoading && (
              <p className="text-gray-600 text-sm mt-4 text-center">
                You can continue adding more approvers. Click 'Close' when finished.
              </p>
            )}
          </form>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  // Generate level title based on current selection
  const getLevelTitle = () => {
    if (approverLevel === 'ProjectLevel') {
      return 'Project Level Approvers';
    } else if (approverLevel === 'FunctionalLevel') {
      return 'Functional Level Approvers' + (functionalLevelDescription ? ' - ' + functionalLevelDescription : '');
    } else {
      return 'Requirement Level Approvers' + (requirementIdWithPrefix ? ' - ' + requirementIdWithPrefix : '');
    }
  };

  return (
<div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
  <div className="bg-white rounded-lg max-w-screen-xl w-full max-h-[90vh] flex flex-col overflow-hidden pb-6">
    <h1 className="text-l font-bold text-black bg-[#CCCCFF] text-center flex items-center justify-center rounded-t-lg" style={{ height: '3em' }}>
      Manage Approvers
    </h1>

        {/* Approver Level Selection Box */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-medium mb-4">Please select the level from the below options:</h3>

          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="approverLevel"
                className="h-5 w-5 mr-3"
                checked={approverLevel === 'ProjectLevel'}
                onChange={() => handleApproverLevelChange('ProjectLevel')}
              />
              <span>Project Level Approvers (Overall project level approvers)</span>
            </label>

            <label className={'flex items-center ' + (!selectedLevelId ? 'opacity-60' : '')}>
              <input
                type="radio"
                name="approverLevel"
                className="h-5 w-5 mr-3"
                checked={approverLevel === 'FunctionalLevel'}
                onChange={() => handleApproverLevelChange('FunctionalLevel')}
                disabled={!selectedLevelId}
              />
              <span>
                Functional Level Approvers: {selectedLevelId ? functionalLevelDescription : 'No functional level selected'}
              </span>
            </label>

            <label className={'flex items-center ' + (selectedRequirements.size !== 1 ? 'opacity-60' : '')}>
              <input
                type="radio"
                name="approverLevel"
                className="h-5 w-5 mr-3"
                checked={approverLevel === 'RequirementLevel'}
                onChange={() => handleApproverLevelChange('RequirementLevel')}
                disabled={selectedRequirements.size !== 1}
              />
              <span>


                Requirement Level Approvers: {
                  selectedRequirements.size === 1
                    ? requirementIdWithPrefix
                    : selectedRequirements.size > 1
                      ? 'More than one requirement selected'
                      : 'No requirement selected'
                }

              </span>
            </label>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className={'mb-4 p-2 rounded ' + (
            error.includes('successfully')
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-500'
          )}>
            {error}
          </div>
        )}

        {/* Title bar */}
        <div className="flex items-center mb-4 justify-between bg-blue-100 p-3 rounded">
          {/* Search bar */}
          <div className="flex items-center">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              placeholder="Search approvers..."
              className="p-2 pr-10 border rounded w-full"
            />
            <button
              onClick={handleSearch}
              className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Search size={18} />
            </button>

            {/* Sort Dropdown */}
            <div className="ml-2 relative">
              <select
                value={sortCriteria}
                onChange={(e) => setSortCriteria(e.target.value)}
                className="p-2 border rounded bg-white text-sm"
              >
                <option value="created_date desc">Created Date (Newest)</option>
                <option value="created_date asc">Created Date (Oldest)</option>
                <option value="user_name asc">User Name (A-Z)</option>
                <option value="user_name desc">User Name (Z-A)</option>
                <option value="approval_user_id asc">User ID (A-Z)</option>
                <option value="approval_user_id desc">User ID (Z-A)</option>
              </select>
            </div>
          </div>

          <h2 className="text-l font-bold text-blue-700 text-center flex-1">{getLevelTitle()}</h2>

          {/* Create New Button with permissions and tooltip */}
          <div className="group relative">
            <button
              disabled={!permissions.canCreate}
              onClick={() => {
                if (permissions.canCreate) {
                  handleOpenCreateDialog();
                }
              }}
              className={'flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ' + (!permissions.canCreate ? 'opacity-50' : '')}
              style={{
                cursor:  'pointer'
              }}
            >
              <Plus size={18} className="mr-2" />
              Create New
            </button>

            {/* Hover tooltip for disabled state */}
            {!permissions.canCreate && (
              <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                Insufficient permission to create approvers
                <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
              </div>
            )}
          </div>
        </div>
        {/* End Title bar */}

        {/* Approvers Table */}
        <div className="flex-1 overflow-auto border border-gray-200 rounded-md">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-3 px-4 text-left">
                  <input
                    type="checkbox"
                    checked={approvers.length > 0 && selectedApprovers.size === approvers.length}
                    onChange={(e) => {
                      const newSelected = new Set();
                      if (e.target.checked) {
                        approvers.forEach(approver => newSelected.add(approver.approval_user_id));
                      }
                      setSelectedApprovers(newSelected);
                    }}
                    disabled={approvers.length === 0}
                    className="h-4 w-4"
                  />
                </th>
                <th className="py-3 px-4 text-left">
                  <div className="flex items-center cursor-pointer" onClick={() => {
                    const newSort = sortCriteria === 'approval_user_id asc' ? 'approval_user_id desc' : 'approval_user_id asc';
                    setSortCriteria(newSort);
                  }}>
                    User ID
                    {sortCriteria.includes('approval_user_id') && (
                      sortCriteria.includes('asc') ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                    )}
                  </div>
                </th>
                <th className="py-3 px-4 text-left">
                  <div className="flex items-center cursor-pointer" onClick={() => {
                    const newSort = sortCriteria === 'user_name asc' ? 'user_name desc' : 'user_name asc';
                    setSortCriteria(newSort);
                  }}>
                    User Name
                    {sortCriteria.includes('user_name') && (
                      sortCriteria.includes('asc') ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                    )}
                  </div>
                </th>
                <th className="py-3 px-4 text-left">
                  <div className="flex items-center cursor-pointer" onClick={() => {
                    const newSort = sortCriteria === 'created_date asc' ? 'created_date desc' : 'created_date asc';
                    setSortCriteria(newSort);
                  }}>
                    Created Date
                    {sortCriteria.includes('created_date') && (
                      sortCriteria.includes('asc') ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                    )}
                  </div>
                </th>
                <th className="py-3 px-4 text-left">Updated Date</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="text-center py-8">Loading...</td>
                </tr>
              ) : approvers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    No approvers found.
                  </td>
                </tr>
              ) : (
                approvers.map((approver) => (
                  <tr key={approver.approval_user_id} className={`border-b border-gray-200 hover:bg-gray-100 ${selectedApprovers.has(approver.approval_user_id) ? 'bg-blue-100' : ''}`}>
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedApprovers.has(approver.approval_user_id)}
                        onChange={() => {
                          const newSelected = new Set(selectedApprovers);
                          if (newSelected.has(approver.approval_user_id)) {
                            newSelected.delete(approver.approval_user_id);
                          } else {
                            newSelected.add(approver.approval_user_id);
                          }
                          setSelectedApprovers(newSelected);
                        }}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>{approver.approval_user_id}</td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>{approver.user_name}</td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>{new Date(approver.created_date).toLocaleDateString()}</td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>{new Date(approver.updated_date).toLocaleDateString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with action buttons and tooltips */}
        <div className="flex justify-between items-center mt-6">
          <div className="flex gap-4">
            {/* Delete Selected Button with permissions and tooltip */}
            <div className="group relative">
              <button
                className={'bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md flex items-center ' + (
                  !permissions.canDelete || selectedApprovers.size === 0 ? 'opacity-50 cursor-not-allowed' : ''
                )}
                onClick={handleDeleteSelectedConfirm}
                disabled={!permissions.canDelete || selectedApprovers.size === 0}
                style={{
                  cursor:   'pointer'
                }}
              >
                <Trash2 size={18} className="mr-2" />
                Delete Selected ({selectedApprovers.size})
              </button>

              {/* Delete button tooltip */}
              {(!permissions.canDelete || selectedApprovers.size === 0) && (
                <div className="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                  {selectedApprovers.size === 0
                    ? "Select one or more approvers to delete"
                    : "Insufficient permission to delete approvers"
                  }
                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-600"></div>
                </div>
              )}
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {selectedApprovers.size > 0 ? selectedApprovers.size + ' selected' : ''}
          </div>

          <button
            className="bg-[#FFFDD0] hover:bg-yellow-200 px-6 py-2 rounded-md"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      {/* Dialogs */}
      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={handleDialogClose}
          onCancel={handleDialogCancel}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {renderDeleteConfirmDialog()}

      {/* Create Approver Dialog */}
      {renderCreateApproverDialog()}
    </div>
  );
};

export default ManageApproversDialog;